import { ACTION_STATUSES, QUERY_PARAMS } from 'shared/consts';
import { BookingStatus } from 'store/bookings/types';
import { displayUnitPriceCents } from 'store/resource/price-tiers';
import { AddOn } from 'store/resource/types';
import { ADD_ONS_DECLINED_PARAM, BOOKING_ADD_ONS_PARAM } from 'hooks/use-booking-add-ons-offer.hook';
import { BookingAddOnSelection, FormDefinition, QuantityViolation } from 'store/booking-add-ons/types';
import { TransactionDetails } from 'store/transactions/types';
import {
  addBookingAddOn,
  removeBookingAddOn,
  getBookingAddOnsMenu,
  markAddOnsOrdered,
  resetBookingAddOns,
  resetSubmitBookingAddOnsStatus,
  clearAddOnForm,
  resolveAddOnForm,
  resolveAddOnRanges,
  setAddOnFormValidity,
  seedBookingAddOns,
  setAddOnFormValues,
  startBookingAddOns,
  submitBookingAddOns,
} from 'store/booking-add-ons/actions';
import { UNATTRIBUTED_PARTY_KEY, groupAddOnsByParty, PartySection } from './group-by-party';
import { bookingAddOnsEligibility } from 'store/booking-add-ons/eligibility';
import { buildAddOnSeed } from 'store/booking-add-ons/seed';
import { buildAddOnSubmission } from 'store/booking-add-ons/build-submission';
import { deriveBounds } from 'store/booking-add-ons/quantity-bounds';
import { buildFormResponse } from 'store/booking-add-ons/build-form-response';
import { findInvalidAnswerKeys } from 'store/booking-add-ons/validate-form-answers';
import { push, replace } from 'store/router/actions';
import { getTransaction, getTransactions } from 'store/transactions/actions';
import { omit } from 'utils/omitObjectProperty';
import { pick } from 'utils/pickObjectProperty';
import qs from 'qs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  bookingAddOnsMenuKey,
  selectAddOnFormForBooking,
  selectAddOnRangesForBooking,
  selectBookingAddOnsMenu,
  selectAddOnsForBooking,
  selectAddOnsAmendingFrom,
  selectAddOnsOrderedPaymentKey,
  selectSubmitBookingAddOnsStatus,
  selectAddOnQuantityViolations,
} from 'store/booking-add-ons/selectors';
import { selectResource } from 'store/resource/selectors';
import { selectTransaction } from 'store/transactions/selectors';
import { useAddOnsEnabled } from 'hooks/use-add-ons-enabled.hook';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocale } from 'hooks/use-locale.hook';
import { FormRendererValidationState } from 'components/vendor-form-renderer/types';
import { useCurrentAdapterName } from 'hooks/use-current-adapter-name';
import { useDispatch, useSelector } from 'react-redux';
import { useGetTransactionById } from 'components/payment/receipt/useGetTransactionById.hook';
import useKioskEnabled from 'store/kiosk/hooks/use-kiosk-enabled';
import { useLocation } from 'react-router-dom';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { useIsPaneLayout } from 'hooks/use-is-pane-layout.hook';
import { addOnsExitPath } from './exit-path';
import { usePostBookingLanding } from 'hooks/use-post-booking-landing.hook';
import { useBookingCompletionSegment } from 'hooks/use-booking-completion-segment.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { paymentIdentity } from 'store/api-client/add-on-payment-types';
import { OrderedAddOnsRead } from 'data/queries/useOrderedAddOnPaymentQuery.hook';
import { AddOnPaymentState, useAddOnPayment } from './use-add-on-payment.hook';
import { RootState } from 'store/reducer';
import { configSelector } from 'store/config/selectors';

interface UseBookingAddOnsReturnValues {
  /* RES-1359. The booking's start, which dates each row's lead-time line. */
  bookingStart?: string;
  booking: TransactionDetails | undefined;
  bookingId: string | null;
  addOns: Array<AddOn>;
  /* RES-1159. One section per owning party, ordered landlord-first then by first appearance. */
  partySections: Array<PartySection>;
  /* The single party the tenant has committed to, or null when nothing is selected. */
  selectedPartyKey: string | null;
  /* ⛔ A convenience, NOT the enforcement — RES-1051 rejects a mixed selection server-side. */
  isPartyLocked: (partyKey: string) => boolean;
  /* RES-1398. Whether this product is one the booking already carries past its notice window. */
  isKept: (addOn: AddOn | undefined) => boolean;
  selectedCount: number;
  /* Minor units. An ESTIMATE, never a charge — the vendor prices the job afterwards. */
  estimateTotal: number;
  /* The currency the total is in, or null when the selection spans more than one. */
  estimateCurrency: string | null;
  /*
   * A line carries no price, so the stated total leaves something out. NOT a null `estimateCurrency`,
   * which also covers a selection spanning currencies; the screens need both apart.
   */
  hasUnpriceable: boolean;
  quantityOf: (addOnId: string) => number;
  /* RES-1241. The product's configured minimum quantity, floored at 1. For pricing an unselected tile. */
  minQuantityOf: (addOnId: string) => number;
  /* RES-1311. Drop a whole line. The fixed-quantity pill's action — never a decrement. */
  onRemove: (addOn: AddOn) => void;
  /* RES-1311. At the product's configured ceiling. ⚠️ For styling only — `onIncrease` holds the guard. */
  isAtMax: (addOnId: string) => boolean;
  /*
   * RES-1311. The product's range admits exactly one quantity, so the row shows an Add/Remove pill
   * instead of a stepper. False whenever no range resolved.
   */
  isFixedQuantity: (addOnId: string) => boolean;
  /* RES-1241. The quantity-range read is in flight, so adding is held until the bands are known. */
  isAwaitingRanges: boolean;
  /* RES-1336. The add-on charge, if billing has opened a session for this order. */
  payment: AddOnPaymentState;
  /*
   * RES-1336. Billing's widget reported a successful charge. ⛔ It does NOT close the sheet — the
   * widget renders its own success screen inside the still-open overlay — it only starts the short
   * poll that waits for the work order to record what was paid.
   */
  onPaymentCharged: VoidFunction;
  /*
   * RES-1336. The order is placed and billing's sheet is still being opened for it. The step moved
   * nowhere — there is no screen of ours between submit and the confirmation — so the CTA that
   * summoned the sheet is what reports the wait, and must stay busy for the whole of it.
   */
  isAwaitingPayment: boolean;
  /* Paid, or the sheet was dismissed. Either way the order stands and the step shows its confirmation. */
  onPaymentSettled: VoidFunction;
  onIncrease: (addOn: AddOn) => void;
  onDecrease: (addOn: AddOn) => void;
  /*
   * Skip. ⭐ Criterion 4 lives here: it makes NO request of any kind and leaves the completed booking
   * untouched, then continues to where the tenant would have landed without the step at all.
   */
  onSkip: VoidFunction;
  /* Hand the selection to the Service Request path. ⛔ Never to the cart. */
  onSubmit: VoidFunction;
  isSubmitting: boolean;
  hasSubmitFailed: boolean;
  /* Lines the submit-time range check refused, with the bounds to show the tenant. */
  rangeViolations: Array<QuantityViolation>;
  isLoading: boolean;
  /*
   * RES-1057. The questions are their own SCREEN but not their own ROUTE — a local step, because
   * `pathname` is load-bearing here in two ways a sibling route would silently break: `onSkip` and the
   * eligibility bounce both rewrite the add-on segment by SUBSTRING to reach the receipt, and
   * `payment-content` decides whether to hide the native mobile header by matching path substrings.
   * A new segment satisfies none of those, and every failure is invisible until a device.
   */
  step: BookingAddOnsStep;
  /* True once a vendor form has been resolved for the chosen party and carries questions to ask. */
  hasForm: boolean;
  /* The resolve is still running. NOT the same as "no form" — see the note at the assignment. */
  isResolvingForm: boolean;
  formDefinition: FormDefinition | null;
  formValues: Record<string, unknown>;
  /* Defaults TRUE, so the form-less majority of bookings are never gated on a signal that never comes. */
  isFormValid: boolean;
  /* Catalog items whose owner has no instance at this building. Surfaced, not blocking. */
  unresolvedAddOns: Array<AddOn>;
  /* RES-1334. An answer contradicts its field's type. Surfaced and blocking — the tenant can fix it. */
  hasInvalidAnswers: boolean;
  onFormChange: (values: Record<string, unknown>) => void;
  onFormValidate: (state: FormRendererValidationState) => void;
  /* The picker's CTA: skip, open the questions, or submit — see `onContinue`. */
  onContinue: VoidFunction;
  onBackToPicker: VoidFunction;
  /* Back to the booking as the tenant left it — distinct from `onSkip`, which ANSWERS the offer. */
  onBackToBooking: VoidFunction;
  /* RES-1326. Non-null only once an order has succeeded; it is what `'confirmation'` renders. */
  orderedAddOns: OrderedAddOns | null;
  /* The confirmation screen's primary action. `onSkip` is still its secondary — back to the booking. */
  onViewMyBookings: VoidFunction;
}

/*
 * ⛔ RES-1336 DELIBERATELY ADDS NO STEP. It briefly had a `payment` one, and that was the mistake:
 * billing's widget is its OWN overlay — a ui-atoms Dialog on desktop, a Drawer on mobile, portaled to
 * `document.body` — so a screen behind it could only ever be an empty surface with its own close
 * control, two modals deep, which is exactly how it rendered. The widget is a DETOUR from the step,
 * not a step, so it opens over whatever is showing and the CTA that summoned it carries the wait.
 *
 * ⚠️ There is no way to host it inline today: billing-admin's `ResponsiveOverlay` hard-wires both
 * surfaces and its public props are `checkoutSessionUuid | open | onOpenChange | onPaymentComplete`
 * plus the context four. Nesting it would need an opt-out there first.
 */
export type BookingAddOnsStep = 'picker' | 'questions' | 'confirmation';

/*
 * Everything the confirmation screen renders, frozen at the moment of a successful submit — the submit
 * clears the slice and re-reads the booking, so the live selection is gone a tick later. It would be
 * unsafe to read live anyway: `submittableSelection` intersects with the RENDERED menu, and a short
 * re-read (RES-1159 saw a partial catalog degrade in production) would empty the lines under the card.
 */
export interface OrderedAddOns {
  booking: TransactionDetails;
  /* Absent in production when a company name does not resolve — the screen handles that. */
  partyName?: string;
  /* VIS-2193. The party's logo, when helix resolved one; the screen falls back to initials. */
  partyLogoUrl?: string;
  lines: Array<BookingAddOnSelection>;
  /* Minor units. Still an ESTIMATE: the vendor prices the job afterwards. */
  total: number;
  /* Null when the amount cannot be stated — mixed currencies, or a quote-required line. */
  currency: string | null;
  /* A line went out unpriced, so `total` covers only part of the order. Frozen with the rest. */
  hasUnpriceable: boolean;
}

/* One place builds a selected line from a catalog add-on, so nothing is enumerated twice. */
const toSelection = (addOn: AddOn, quantity: number): BookingAddOnSelection => ({
  id: addOn.id,
  name: addOn.name,
  /*
   * RES-1241. For a tiered product this is the band that applies at this quantity, not the scalar
   * `price` helix sent - that one is a from-price for the tile.
   *
   * Resolving here rather than at each site that renders money is what makes the rest of the step
   * correct for free: every quantity change re-enters through `toSelection`, so `estimateTotal`, the
   * ordered screen's subtotal, the frozen snapshot behind it and the CTA all stay right without
   * knowing bands exist. Resolving at those sites would be four places to keep in step, and the
   * snapshot would have captured a pre-band figure.
   */
  price: displayUnitPriceCents(addOn, quantity),
  currency: addOn.currency,
  description: addOn.description,
  image_url: addOn.image_url,
  required: addOn.required,
  owning_party: addOn.owning_party?.toLowerCase(),
  /* Carried so the totals can tell an unpriceable line from a genuinely free one. */
  pricing_type: addOn.pricing_type,
  quantity,
});

/*
 * RES-1050 — hydration, the entry guard and the selection for the post-booking add-on step.
 *
 * ⭐ THE ENTRY IS THE ASSERTION THAT MATTERS on this ticket: the step must be reachable only after a
 * booking exists, and only for a booking that may actually carry add-ons. It is keyed on a booking id in
 * the URL and resolved lookup-then-bounce, the shape RES-999 established for
 * `/booking-details/:transactionId`.
 *
 * ⛔ The eligibility rules are the SAME ones the post-payment detour applies — a URL is a door, so a
 * tenant arriving by reload, back button or pasted link is held to identical conditions.
 */
export const useBookingAddOns = (): UseBookingAddOnsReturnValues => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const isPaneLayout = useIsPaneLayout();

  /* RES-1367 — where the booking IS, which is not always the receipt. The rule, and why, live there. */
  /* RES-1407: `receipt` with the flag off; `booked` (the offer or My bookings, no receipt) with it on. */
  const completionSegment = useBookingCompletionSegment();
  const bookingPath = addOnsExitPath({ pathname, isPaneLayout, completionSegment });
  const searchParams = useSearchParams<Record<string, string | undefined>>();
  const bookingIdParam = searchParams?.[BOOKING_ADD_ONS_PARAM] ?? '';

  const storeTransaction = useSelector(selectTransaction);
  const resource = useSelector(selectResource);
  const isKioskMode = useKioskEnabled();
  const adapterName = useCurrentAdapterName();
  const addOnsEnabled = useAddOnsEnabled();
  const { ownerType, ownerUuid } = useOwnerParams();

  /*
   * Prefer the transaction already in the store — on the detour path the checkout just fetched it.
   * ⚠️ AND PREFERRING IT MATTERS. `ApiClient.getTransactions`, which the fallback hook drives, hardcodes
   * `is_upcoming=true`; a booking whose start passes while the tenant is choosing catering drops out of
   * that list, the lookup misses, and the page would bounce mid-selection.
   */
  const storeMatches =
    storeTransaction?.details?.id != null && String(storeTransaction.details.id) === String(bookingIdParam);

  const {
    transaction: fetchedTransaction,
    isLoading: isLoadingBooking,
    isNotFound,
  } = useGetTransactionById(storeMatches ? '' : bookingIdParam);

  const booking = storeMatches ? storeTransaction : fetchedTransaction;
  const resourceId = booking?.details?.resource_id;

  const bookingStart = booking?.details?.start_at;
  const menuKey = resourceId && bookingStart ? bookingAddOnsMenuKey(resourceId, bookingStart) : null;
  const menu = useSelector(selectBookingAddOnsMenu);

  /*
   * ACCESS-2591. ⛔ RE-READ THE ROOM EVEN WHEN THE STORE ALREADY HOLDS IT WITH ADD-ONS.
   *
   * The detour arrives with the resource already in the store — but it was read during CHECKOUT, before
   * the booking existed, so helix had no start to measure each product's minimum notice against and the
   * add-ons on that payload are unfiltered. So the step reads the room again with the booking's start, into
   * its own slot (see `BookingAddOnsState.menu`), and renders only that. The shared slot is used only for a
   * booking with no start, which has nothing to filter against.
   *
   * ⚠️ A failed read, or no owner to read with, leaves no room for this step, so the booking is ineligible
   * and bounces to the receipt — the tenant is never offered the stale, unfiltered menu.
   *
   * ⛔ Read once per MOUNT, not once per session: notice windows close as the booking approaches, so a
   * menu landed earlier in the session is not reused. The ref, not the store key, is what dedupes.
   */
  const canReadMenu = Boolean(menuKey && ownerType && ownerUuid);
  const requestedMenuKeyRef = useRef<string | null>(null);
  const hasRequestedMenu = canReadMenu && requestedMenuKeyRef.current === menuKey;
  const menuResource = hasRequestedMenu && menu.key === menuKey ? menu.resource : null;
  const effectiveResource = menuKey ? menuResource : resource;

  const { isEligible, bookingId, addOns } = useMemo(
    () => bookingAddOnsEligibility({ booking, resource: effectiveResource, adapterName, addOnsEnabled, isKioskMode }),
    [booking, effectiveResource, adapterName, addOnsEnabled, isKioskMode],
  );

  useEffect(() => {
    if (canReadMenu && menuKey && requestedMenuKeyRef.current !== menuKey) {
      requestedMenuKeyRef.current = menuKey;
      dispatch(
        getBookingAddOnsMenu.request({
          key: menuKey,
          params: {
            ownerType,
            ownerUuid,
            resourceUuid: String(resourceId),
            getResourceOptions: { start_at: bookingStart },
          },
        }),
      );
    }
  }, [dispatch, canReadMenu, menuKey, ownerType, ownerUuid, resourceId, bookingStart]);

  /*
   * ⛔ SETTLED, NOT SUCCEEDED. A read that fails must stop the page loading rather than spin forever —
   * the tenant has already paid, and an endless skeleton is the one outcome that cannot be recovered from.
   * And settled for THIS mount's read: an earlier mount's FULFILLED for the same key must not count.
   */
  const isResourceSettled =
    !canReadMenu ||
    (hasRequestedMenu &&
      menu.key === menuKey &&
      (menu.status === ACTION_STATUSES.FULFILLED || menu.status === ACTION_STATUSES.REJECTED));

  /*
   * ⭐ ONE PIECE OF STATE, THREE JOBS: it is what the confirmation screen renders, and it is also the
   * "this booking has been ordered" signal that suppresses the eligibility bounce and the `booking`
   * render gate below. A separate boolean flag could disagree with the snapshot; this cannot.
   */
  const [orderedAddOns, setOrderedAddOns] = useState<OrderedAddOns | null>(null);

  /* Lookup-then-bounce. A crafted, stale or ineligible id resolves to nothing and the page does not render. */
  useEffect(() => {
    /*
     * ⛔ NOT AFTER A SUCCESSFUL ORDER. The submit re-reads the booking precisely so it now carries
     * add-ons, which makes eligibility condition 8 fail — so without this guard the tenant's own order
     * would immediately replace their confirmation screen with the receipt.
     */
    if (orderedAddOns) {
      return;
    }

    if (isNotFound || (isResourceSettled && !isLoadingBooking && booking && !isEligible)) {
      /*
       * ⛔ To the RECEIPT, not into browser history. `goBack()` would return a post-payment tenant into
       * the payment flow they just completed, and a direct or reloaded entry to some unrelated page. The
       * receipt is where a skipped step lands, so it is where a refused one belongs too.
       */
      dispatch(replace(`${bookingPath}${search}`));
    }
  }, [
    dispatch,
    isNotFound,
    isResourceSettled,
    isLoadingBooking,
    booking,
    isEligible,
    bookingPath,
    search,
    orderedAddOns,
  ]);

  useEffect(() => {
    if (bookingId && isEligible) {
      dispatch(startBookingAddOns(bookingId));
    }
  }, [dispatch, bookingId, isEligible]);

  /*
   * Opens the picker on what is already ordered: the submit sends `items` as the complete selection
   * and helix replaces the booking's rows with it, so an empty picker would delete the order. Separate
   * from the claim effect above because the seed needs the menu to match against; `isResourceSettled`
   * is absent from the guard because `isEligible` subsumes it. Priced through `toSelection` so a
   * tiered line carries the band for its quantity rather than its from-price.
   */
  const orderedVendorAddOns = booking?.details?.vendor_add_ons;

  const bookedQuantities = useMemo(() => {
    const booked = new Map<string, number>();

    /*
     * Summed, not last-wins, because `buildAddOnSeed` sums: two rows for one product is one product
     * ordered twice. A unique on (catalog_item_uuid, resource_booking_details_id) makes that
     * unreachable today, but the ceiling and the seed have to agree without depending on a
     * constraint in another repo to keep them that way.
     */
    (orderedVendorAddOns ?? []).forEach(row => {
      const key = row?.catalog_item_uuid?.toLowerCase();
      const quantity = Number(row?.quantity);

      if (key && Number.isFinite(quantity) && quantity > 0) {
        booked.set(key, (booked.get(key) ?? 0) + quantity);
      }
    });

    return booked;
  }, [orderedVendorAddOns]);

  /*
   * RES-1398. The ceiling a kept line is held to, by menu id -- present only for products that are
   * actually being kept. The selection slice outlives a menu read (it is booking-keyed and the seed
   * latches on `seededFor`), so a tenant can raise a line while the product is orderable and come
   * back to a menu that has since flagged it. Without this the client would submit the raised
   * quantity and helix, which caps the exemption at what the booking holds, would refuse the lot.
   */
  const keptCeilings = useMemo(() => {
    const ceilings = new Map<string, number>();

    addOns.forEach(addOn => {
      const booked = bookedQuantities.get(addOn?.id?.toLowerCase() ?? '');

      if (addOn?.unavailable_reason === 'minimum_notice' && booked !== undefined && Number.isFinite(booked)) {
        ceilings.set(addOn.id, booked);
      }
    });

    return ceilings;
  }, [addOns, bookedQuantities]);

  /*
   * RES-1398. A line the booking already carries survives its notice window closing.
   *
   * The reason is tested exactly, never for truthiness. Every condition that makes the read say
   * `price_unavailable` also throws on helix's submit path, so exempting one would not merely show
   * an estimate for something nobody has priced -- it would send a line the server refuses.
   */
  const isKept = useCallback(
    (addOn: AddOn | undefined): boolean => Boolean(addOn?.id) && keptCeilings.has(addOn?.id ?? ''),
    [keptCeilings],
  );

  useEffect(() => {
    if (!bookingId || !isEligible) {
      return;
    }

    /*
     * RES-1359. Seeded off what can actually be submitted, not off the whole menu.
     *
     * helix used to drop a product whose notice window had closed, so the seed never matched one and
     * an amend simply lost it. It now returns them flagged, and seeding one would pre-fill a line
     * that `liveSelection` excludes from the submission -- a row showing a quantity and a remove
     * control while silently not being kept.
     *
     * RES-1398 is that keeping: the menu handed to the seed now includes a flagged product the
     * booking already carries, so the row is pre-filled with the quantity that will be submitted.
     * The seed only emits rows present in the order, so widening the menu cannot pre-fill anything
     * the tenant does not already own.
     */
    const { matches, formResponse } = buildAddOnSeed(
      orderedVendorAddOns,
      addOns.filter(addOn => !addOn?.unavailable_reason || isKept(addOn)),
    );

    if (!matches.length) {
      return;
    }

    dispatch(
      seedBookingAddOns({
        bookingId,
        addOns: matches.map(({ addOn, quantity }) => toSelection(addOn, quantity)),
        formValues: formResponse?.values ?? null,
        formTemplateUuid: formResponse?.form_template_uuid ?? null,
      }),
    );
  }, [dispatch, bookingId, isEligible, orderedVendorAddOns, addOns, isKept]);

  const selected = useSelector(state => selectAddOnsForBooking(state as never, bookingId));
  const submitStatus = useSelector(selectSubmitBookingAddOnsStatus);

  const partySections = useMemo(() => groupAddOnsByParty(addOns), [addOns]);

  /*
   * The selection as it exists ON SCREEN -- selected lines intersected with the rendered menu.
   *
   * The slice is booking-keyed and survives a resource re-fetch, but rows come off the catalog
   * payload, so the two can diverge for one booking: helix may stop returning a product it returned
   * before (`resolveItems` filters `status: ['active']`, and a partial degrade can drop one party's
   * chunk). An orphaned line -- selected with no row on screen -- would lock every section with
   * nothing visible to clear (this step ships no reset), and would ride along on submit, letting the
   * client post a two-party selection that RES-1051 assumes it cannot produce.
   *
   * So the lock, the totals and the submission all read this one derived array.
   *
   * RES-1359 widened this from "rendered" to "orderable". helix used to drop a product whose notice
   * window had closed, so a line chosen while orderable cleared itself once its id left the menu. The
   * id now stays, flagged, so without this the line survives the window closing, keeps its quantity
   * through a remount, and reaches `buildAddOnSubmission` -- whose only gate is `quantity > 0`.
   *
   * RES-1398 splits the two jobs this map was doing. Menu membership still drops an orphan, because
   * the submit replaces every row and a line with nothing on screen would ride along unseen. The
   * notice exclusion is now per line, so a product the booking already carries is kept.
   */
  const liveSelection = useMemo(() => {
    const rendered = new Map(addOns.map(addOn => [addOn?.id, addOn]));

    return selected
      .filter(item => {
        const live = rendered.get(item.id);

        return Boolean(live) && (!live?.unavailable_reason || isKept(live));
      })
      .map(item => {
        /*
         * RES-1398. A kept line is held to what the booking carries. The row below reads the same
         * ceiling, so what is shown and what is sent cannot disagree.
         */
        const ceiling = keptCeilings.get(item.id);
        /*
         * Re-priced, not just re-counted. `toSelection` resolves a tiered line's band from its
         * quantity, and every other quantity change re-enters through it; holding the quantity down
         * here without doing the same would leave a band price from a quantity nobody is submitting.
         */
        const held =
          ceiling !== undefined && item.quantity > ceiling
            ? {
                ...item,
                quantity: ceiling,
                price: displayUnitPriceCents(rendered.get(item.id) ?? item, ceiling),
              }
            : item;

        /*
         * `pricing_type` is REFRESHED from the menu, not read off the stored line. The line keeps
         * whatever it carried when it was added, so a product the vendor has since moved to
         * quote-required would still be summed into the estimate and would still offer a plain
         * submit -- the tenant would be shown a figure for something nobody has priced. The same
         * staleness is why `selectedPartyKey` below resolves through the sections rather than
         * trusting the line's own `owning_party`.
         *
         * Only this field. A stale PRICE is the pre-existing behaviour of this step and changing it
         * would move the figure under someone mid-selection; a stale TYPE changes which flow they
         * are in, which is this ticket's concern.
         *
         * The row's own identity is returned untouched when nothing moved, so the common path adds
         * no new object for the memos downstream to see as a change.
         */
        /*
         * An ABSENT type on the menu row is not an answer. `pricing_type` is optional on the
         * payload, so reading it straight would write `undefined` over a stored `quote_required`
         * and drop the line back into the priced sum -- the same misstatement this refresh exists
         * to prevent, reached from the other side. The stored value is the better of two
         * imperfect answers when the menu has none.
         */
        const live = rendered.get(item.id);
        const pricingType = live?.pricing_type ?? item.pricing_type;

        return pricingType === held.pricing_type ? held : { ...held, pricing_type: pricingType };
      });
  }, [addOns, selected, isKept, keptCeilings]);

  /*
   * A pure function of the live selection, recomputed on every change. Nothing latches: removing the
   * last add-on unlocks the page, and removing one of several leaves the others locked.
   *
   * Resolved through the SECTIONS, by add-on id -- not off the stored line's `owning_party`. A stored
   * line keeps the party it carried when added, so if the catalog moves that product to another
   * company, everything would lock against a key nothing on screen matches.
   */
  const selectedPartyKey = useMemo(() => {
    if (!liveSelection.length) {
      return null;
    }

    const { id } = liveSelection[0];

    return partySections.find(section => section.addOns.some(addOn => addOn.id === id))?.key ?? null;
  }, [liveSelection, partySections]);

  const selectedPartyName = useMemo(
    () => partySections.find(section => section.key === selectedPartyKey)?.name,
    [partySections, selectedPartyKey],
  );

  const selectedPartyLogoUrl = useMemo(
    () => partySections.find(section => section.key === selectedPartyKey)?.logoUrl,
    [partySections, selectedPartyKey],
  );

  const isPartyLocked = useCallback(
    (partyKey: string) => selectedPartyKey !== null && partyKey !== selectedPartyKey,
    [selectedPartyKey],
  );

  /*
   * The one-party invariant, made structural rather than relying on `onIncrease`'s guard.
   *
   * RES-1051 builds its `409` on the premise that "a mixed-party selection arriving at this endpoint
   * is not a client-state bug, it is a submission the client cannot produce". So whatever sits in the
   * slice, only the chosen party's lines are ever submitted or totalled.
   */
  const submittableSelection = useMemo(() => {
    /*
     * Membership of the RENDERED section, not a comparison against the line's stored `owning_party`:
     * comparing those two sources drops every line whose product's party has since changed, including
     * a stored party against a catalog add-on that carries none. One source of truth, keyed by id.
     */
    const selectedSection = partySections.find(section => section.key === selectedPartyKey);

    if (!selectedSection) {
      return [];
    }

    const idsInSection = new Set(selectedSection.addOns.map(addOn => addOn.id));

    return liveSelection.filter(item => idsInSection.has(item.id));
  }, [liveSelection, partySections, selectedPartyKey]);

  const quantityOf = useCallback(
    (addOnId: string) => {
      const stored = selected.find(item => item.id === addOnId)?.quantity ?? 0;
      /* The same ceiling `liveSelection` holds a kept line to; raw for every other line. */
      const ceiling = keptCeilings.get(addOnId);

      return ceiling !== undefined ? Math.min(stored, ceiling) : stored;
    },
    [selected, keptCeilings],
  );

  const ranges = useSelector((state: RootState) => selectAddOnRangesForBooking(state, bookingId));

  /*
   * RES-1241 — adding is held while the quantity-range read is in flight.
   *
   * ⛔ THE RACE THIS CLOSES IS A WRONG PRICE, NOT A WRONG QUANTITY. A line is priced by
   * `toSelection` at the quantity it is created with. Add a tiered product before its range lands and
   * that is quantity 1; the range then raises the line to the product's minimum, and if that minimum
   * sits in a different band the stored price is the one for a quantity nobody ordered. The row
   * re-prices from the catalog as it renders, so it shows the right figure while the estimate, the
   * submitted order and the confirmation all carry the stale one — $40 on screen, $45 submitted.
   *
   * ⚠️ `PENDING` ONLY, never `status === null`. Null is also the resting state of a menu whose read is
   * never requested — no item carries an `owning_party`, or there is no building — and treating that
   * as "waiting" would disable the stepper on those menus forever. The reducer writes PENDING on the
   * request action itself, so this covers the whole in-flight window from the dispatch onward.
   *
   * Fails open on REJECTED, like every other range-derived value here: a failed read leaves the
   * product orderable at today's unbounded behaviour rather than stranding the menu.
   */
  const isAwaitingRanges = ranges.status === ACTION_STATUSES.PENDING;
  const rangeViolations = useSelector((state: RootState) => selectAddOnQuantityViolations(state, bookingId));

  /*
   * RES-1311. The two derived numbers every control below branches on. ⛔ Derive them ONCE and never
   * branch on the raw columns: a zero minimum means ONE to work-orders, whose floor is
   * `Math.max(1, minQuantity)`, and a null maximum means UNBOUNDED rather than a ceiling of zero. A
   * control built from the raw values offers a quantity of zero as valid and earns a 400.
   *
   * ⚠️ No resolved range means today's behaviour — floor of one, no ceiling — NOT a refusal. The read
   * fails open, and a tenant lacking `CAN_MANAGE_TENANT_WORK_ORDERS` never gets one at all.
   *
   * The ceiling is floored at the minimum so an inverted range can never make `max < min`. Their
   * settings API rejects inverted ranges, but nothing here should rest on a remote validation.
   *
   * ⛔ `max` AND `ceiling` ARE DIFFERENT NUMBERS, AND THEIR CONSUMERS ARE NOT INTERCHANGEABLE.
   * `max` is what the landlord configured — Infinity when unbounded OR not knowable — and the pill
   * branch reads it, so an unknown range can never collapse a stepper into a toggle. `ceiling` is what
   * the control may reach RIGHT NOW. It drops to 1 only while the read is IN FLIGHT: a tenant tapping
   * + four times inside that round trip would otherwise sail past a maximum of two and lose the whole
   * order at the worker. 1 is safe for every product because their settings API refuses a maximum of
   * zero.
   *
   * ⛔ THE CAP IS KEYED ON THE REQUEST'S STATUS, NEVER ON THE RANGE BEING ABSENT, and the difference
   * is a production outage rather than a nuance. `range == null` is true in FOUR states: not yet
   * requested, in flight, the read failed, and the read succeeded but carried nothing for this item —
   * which is every item until work-orders deploys the field. Capping on absence would pin every
   * product at a quantity of 1 in all four, permanently in the last two, and would turn the
   * deploy-order safety of this change (it falls back to today's behaviour until work-orders ships)
   * into a menu where nothing can be ordered in quantity. So: absent AND settled means unbounded with
   * a floor of 1, which is exactly what shipped before this feature.
   */
  const boundsFor = useCallback(
    (addOnId: string) => {
      const range = ranges.byCatalogItemUuid[addOnId.toLowerCase()];
      const { min, max } = deriveBounds(range);
      const awaitingRead = ranges.status === null || ranges.status === ACTION_STATUSES.PENDING;

      return { min, max, ceiling: range == null && awaitingRead ? 1 : max };
    },
    [ranges],
  );

  /*
   * RES-1241. The configured minimum, for pricing an unselected tiered tile: quantity 0 matches no
   * band, so the row prices at the smallest quantity the tenant could order.
   */
  const minQuantityOf = useCallback((addOnId: string) => boundsFor(addOnId).min, [boundsFor]);

  /* Reads `ceiling`, not `max`, so the greyed + matches what `onIncrease` will actually refuse. */
  const isAtMax = useCallback(
    (addOnId: string) => quantityOf(addOnId) >= boundsFor(addOnId).ceiling,
    [quantityOf, boundsFor],
  );

  /*
   * ⛔ An unresolved range cannot read as fixed, and the DEFAULTS guarantee that, not this predicate:
   * `boundsFor` falls back to floor 1 / ceiling Infinity, which can never be equal. Change either and
   * every unranged product reads as fixed, stripping the stepper from any tenant whose read is refused.
   * `request_quantity` is NOT consulted — the design draws this app's only no-qty product as a switch.
   */
  const isFixedQuantity = useCallback(
    (addOnId: string) => {
      const { min, max } = boundsFor(addOnId);

      return Number.isFinite(max) && min === max;
    },
    [boundsFor],
  );

  /*
   * The grey-out is enforced HERE, not by the control. `Counter`'s `isIncreaseDisabled` reaches only
   * the ICON, the icons carry `pointer-events: none`, and the blocks binding `onClick` have no
   * disabled state -- so a "disabled" plus still fires. The row withholds the counter when locked;
   * this guard is the second half, so the assertion never rests on the render. Same reason the
   * quantity floor is held here rather than by `isDecreaseDisabled`.
   */
  const onIncrease = useCallback(
    (addOn: AddOn) => {
      if (isPartyLocked(addOn.owning_party?.toLowerCase() ?? UNATTRIBUTED_PARTY_KEY)) {
        return;
      }

      /*
       * The real guard, for the same reason the party lock is enforced here rather than left to the
       * icon: the row greys the + and leaves the press bound, so a styling-only hold still adds — at
       * the wrong price. Neither half may depend on the other being right.
       */
      if (isAwaitingRanges) {
        return;
      }

      /* The row withholds the control; this is the half that enforces it. */
      if (addOn.unavailable_reason) {
        return;
      }

      const { min, ceiling } = boundsFor(addOn.id);
      const current = quantityOf(addOn.id);

      /*
       * The real guard. `isIncreaseDisabled` greys the icon and nothing more, so a styling-only cap
       * looks right and still adds — the same reason the party lock is enforced here. `ceiling`, not
       * `max`: until the range resolves it is 1, which no configured maximum can be below.
       */
      if (current >= ceiling) {
        return;
      }

      /* Entering the order at the product's required quantity, not at one. */
      dispatch(addBookingAddOn(toSelection(addOn, Math.min(current === 0 ? min : current + 1, ceiling))));
    },
    [dispatch, quantityOf, isPartyLocked, boundsFor, isAwaitingRanges],
  );

  /*
   * ⛔ NOT a decrement. A grandfathered line can sit ABOVE what its range now admits — five of
   * something narrowed to two — and stepping down by one leaves four while the label says removed.
   */
  const onRemove = useCallback(
    (addOn: AddOn) => {
      dispatch(removeBookingAddOn(addOn.id));
    },
    [dispatch],
  );

  const onDecrease = useCallback(
    (addOn: AddOn) => {
      const next = quantityOf(addOn.id) - 1;
      /*
       * RES-1311. The floor moved from one to the product's minimum, and going under it REMOVES the
       * line rather than clamping: a product required in threes has no valid quantity of two, so the
       * only thing below the floor is not ordering it. Which is what this already did at zero.
       */
      const { min } = boundsFor(addOn.id);

      dispatch(next >= min ? addBookingAddOn(toSelection(addOn, next)) : removeBookingAddOn(addOn.id));
    },
    [dispatch, quantityOf, boundsFor],
  );

  /*
   * ⚠️ A single total is only meaningful in a single currency. A room may offer several parties' products,
   * and RES-1049 emits currency per item — so summing across currencies would render a number that means
   * nothing. When the selection spans more than one, the amount is withheld rather than invented.
   */
  const { estimateTotal, estimateCurrency, hasUnpriceable } = useMemo(() => {
    /*
     * ⚠️ EVERY line must carry the SAME currency. `AddOn.currency` is optional — absent on every Prism and
     * Nexudus add-on, and on native ones until RES-1049 ships the field — so filtering the blanks out first
     * would let a USD item and a currency-less item render their combined value as USD.
     */
    /*
     * RES-1357. A quote-required line is left out of the sum rather than added as its stored zero:
     * summing it understated the order, and withholding the whole figure hid a subtotal we do know.
     * Service Request estimates over priced lines alone for the same reason.
     *
     * A quote-only basket has no priced line, so no currency, so the amount is still withheld - that
     * falls out of the currency rule below rather than needing a branch.
     *
     * Only this pricing type is excluded. A free product's zero is a figure someone set.
     */
    const pricedSelection = submittableSelection.filter(item => item.pricing_type !== 'quote_required');

    const currencies = pricedSelection.map(item => item.currency);
    const first = currencies[0];
    const isOneCurrency = Boolean(first) && currencies.every(currency => currency === first);

    const hasQuoteRequiredLine = submittableSelection.length > pricedSelection.length;

    return {
      estimateTotal: pricedSelection.reduce((sum, item) => sum + item.price * item.quantity, 0),
      estimateCurrency: isOneCurrency ? (first as string) : null,
      /*
       * ⚠️ Returned separately because it is NOT interchangeable with `estimateCurrency === null`,
       * which also goes null for a selection spanning currencies. Whether a total can be DISPLAYED and
       * whether money is OWED are different questions — see the landing step.
       */
      hasUnpriceable: hasQuoteRequiredLine,
    };
  }, [submittableSelection]);

  /* ─── RES-1057: the vendor's form ─────────────────────────────────────────────────────────── */

  const locale = useLocale();
  /*
   * ⚠️ The BUILDING uuid, not `ownerUuid`. work-orders scopes the owner-forms read on the building and
   * refuses one outside the caller's scopes, and `useOwnerParams` can resolve `ownerUuid` to a company
   * on some paths. The config is the same source every other read in this app uses for the building.
   */
  const { buildingUuid } = useSelector(configSelector);

  /*
   * RES-1336 — the charge behind this booking's add-ons, if there is one.
   *
   * `booking.details.id` is the key the add-on-payments read takes: the same id the admin
   * service-request-links card uses, because it is the only one a client holds. Numeric — the type
   * admits a string, and the path builder would happily interpolate one into a 404.
   */
  /*
   * ⛔ THE ID OF THE ORDER PLACED, CAPTURED AT SUBMIT — not read live off `booking`, which is the
   * whole point. Set only for a payable order: one stating a real amount, a total above zero in ONE
   * currency. A zero total is free and a null currency is the existing "do not state an amount"
   * channel for a quote-required line; neither produces a checkout session, so neither should hold
   * the tenant on a wait. Null is therefore both "nothing to pay" and "nothing ordered yet", and it
   * gates the read.
   *
   * ⛔ IT IS A SNAPSHOT BECAUSE THE LIVE BOOKING DOES NOT SURVIVE THE SUBMIT. The same handler
   * re-reads the booking, and the tenant lands on the confirmation — which renders its own frozen
   * snapshot for exactly this reason. Keying the read off `booking?.details?.id` meant that the
   * moment the order was placed the id went away, the query turned ENABLED → DISABLED, and the poll
   * stopped: no further requests, and not even the 45s deadline, which only runs inside the query
   * function. Measured on the preview — "Preparing your payment…" sat there for two and a half
   * minutes with two requests on the wire and a live session waiting on the other side.
   *
   * ⚠️ Numeric. The type admits a string and the path builder would happily interpolate one into a 404.
   */
  const [payableBookingId, setPayableBookingId] = useState<number | null>(null);

  /*
   * ⛔ THE WIDGET SAID A CHARGE WENT THROUGH, which this app cannot work out for itself: the work
   * order reads `pending_payment` both before a payment and in the moments after one, so without
   * this the read would either stop too early — leaving the confirmation calling a settled charge an
   * estimate — or poll on every order a tenant simply walked away from.
   */
  const [hasCharged, setHasCharged] = useState(false);
  const onPaymentCharged = useCallback(() => setHasCharged(true), []);
  /*
   * RES-1336 — billing's sheet has been closed, set by `onPaymentSettled` below, which is the single
   * exit from it. Passed to the query as the fallback signal for a charge that was made but never
   * reported; it is NOT a claim that anything was paid.
   */
  const [hasDismissedPayment, setHasDismissedPayment] = useState(false);
  /*
   * The baseline this screen's money is held against. In the store rather than local state because
   * the picker route unmounts between the submit and this screen -- as `lastOrdered` already is.
   */
  const orderedPaymentKey = useSelector((state: RootState) => selectAddOnsOrderedPaymentKey(state, payableBookingId));
  /* Stamped by the card on its way out, the only place the pre-amend money is reliably in hand -- see
   * `markAddOnsAmending`. The cache read at the submit is the fallback for any route that reaches the
   * picker without passing through the card. */
  const amendingFrom = useSelector((state: RootState) => selectAddOnsAmendingFrom(state, bookingId));
  const payment = useAddOnPayment(
    buildingUuid ?? null,
    payableBookingId,
    payableBookingId !== null,
    hasCharged,
    hasDismissedPayment,
    orderedPaymentKey,
  );
  const [step, setStep] = useState<BookingAddOnsStep>('picker');

  /*
   * RES-1367 — ⛔ TELL THE BOOKING'S OWN ADD-ONS CARD WHAT JUST HAPPENED TO THE CHARGE.
   *
   * There are TWO queries onto the same endpoint and they are deliberately not one: this flow's asks
   * "will a session arrive for the order I just placed" and carries a poll, two deadlines and a focus
   * refetch to answer it; the booking-details card's asks "what happened to this order" and carries
   * none of that. They return DIFFERENT SHAPES from the same URL, so sharing a key would serve one of
   * them an answer it cannot read -- see `useOrderedAddOnPaymentQuery`, which says so.
   *
   * What was missing is the link between the two caches. Paying in this flow updated ours and left
   * the card underneath holding its own cached "unpaid", so a tenant who paid and closed the sheet
   * watched the booking behind it go on saying the charge was outstanding until they navigated away
   * and back -- which remounts, and refetches. Reported by Mason on RES-1367.
   *
   * ⚠️ ON THE CHARGE LANDING, not on `onPaymentCharged`. That fires the moment billing's widget
   * reports success, which is BEFORE the work order records it -- re-reading then just caches
   * "unpaid" a second time. `payment.charge` is the settled record, so it is the honest moment.
   *
   * ⚠️ And on DISMISSAL too, which is the weaker half for the same reason the query takes it: a
   * charge can be made without the callback ever reaching us. Keyed on a boolean rather than the
   * charge object, which is a fresh identity on every poll and would re-invalidate forever.
   */
  const queryClient = useQueryClient();
  const hasSettledCharge = Boolean(payment.charge);

  useEffect(() => {
    if (!buildingUuid || !payableBookingId || (!hasSettledCharge && !hasDismissedPayment)) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ['ordered-add-on-payment', buildingUuid, payableBookingId],
    });
  }, [queryClient, buildingUuid, payableBookingId, hasSettledCharge, hasDismissedPayment]);

  /*
   * ⛔ THE WAIT HAS TO END SOMEWHERE, AND THERE IS NO SCREEN TO END IT ON ANY MORE. While a payable
   * order has no session yet the tenant sits on the screen they submitted from with a busy CTA; if
   * the deadline runs out, the link failed, or the read was refused, no sheet is ever going to open
   * and leaving them on a spinning button is the dead end the removed `payment` screen used to be.
   * The order IS placed either way, so the confirmation is the honest landing.
   *
   * ⚠️ Fires once: it moves the step, which stops `isAwaitingPayment` mattering.
   */
  const hasGivenUpOnPayment = payableBookingId !== null && !payment.isAwaitingSession && !payment.checkoutSessionUuid;

  useEffect(() => {
    if (hasGivenUpOnPayment) {
      setStep('confirmation');
    }
  }, [hasGivenUpOnPayment]);

  const form = useSelector((state: RootState) => selectAddOnFormForBooking(state, bookingId));

  /*
   * RES-1311. The range read's payload: the WHOLE rendered menu, not the selection.
   *
   * ⛔ THE DIFFERENCE FROM `formItems` IS THE POINT, not an inconsistency. A form is only needed for
   * the party the tenant chose, so that read is keyed on the selection. A range has to be known BEFORE
   * the first product is added, because adding one commits a quantity — so this read cannot wait for a
   * selection to exist.
   *
   * ⚠️ `owning_party` is sent RAW here. work-orders echoes the selection back through a case-sensitive
   * JS key built from its own stored casing, so a lower-cased owner uuid drops the item from every
   * group and reports as "nobody owns this here". This app lower-cases that value in three other
   * places, so the catalog `AddOn` is the only place the original survives.
   */
  const rangeItems = useMemo(
    () =>
      addOns
        .filter(addOn => addOn.owning_party)
        .map(addOn => ({ catalogItemUuid: addOn.id, ownerCompanyUuid: addOn.owning_party as string })),
    [addOns],
  );

  /*
   * Keyed on the item SET so a quantity change does not re-resolve, and on the BOOKING, which is not
   * redundant: the step moving to a different booking with the SAME menu clears `ranges` while an
   * item-set-only ref still matches, so the request is skipped and that booking stays unbounded.
   */
  const rangeItemsKey = useMemo(
    () =>
      rangeItems.length === 0
        ? ''
        : [bookingId ?? '', ...rangeItems.map(item => `${item.catalogItemUuid}:${item.ownerCompanyUuid}`).sort()].join(
            '|',
          ),
    [rangeItems, bookingId],
  );
  const requestedRangeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEligible || !buildingUuid || !bookingId || !rangeItemsKey) {
      return;
    }

    if (requestedRangeKeyRef.current === rangeItemsKey) {
      return;
    }

    requestedRangeKeyRef.current = rangeItemsKey;
    dispatch(resolveAddOnRanges.request({ buildingUuid, items: rangeItems, bookingId, requestKey: rangeItemsKey }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isEligible, buildingUuid, bookingId, rangeItemsKey]);

  /*
   * ⛔ THE OWNER-FORMS PAYLOAD IS BUILT FROM THE CATALOG `AddOn`, NOT FROM THE STORED SELECTION, AND
   * THE CASING IS THE REASON.
   *
   * work-orders echoes the selection back through a JS template-literal key built from ITS OWN stored
   * casing and tests it against a set built from ours. The database join is collation-case-insensitive
   * and matches; that JS comparison is not. A lower-cased uuid therefore drops the item from every
   * group and surfaces as "nobody owns this at this building" — a false positive shaped exactly like
   * the real failure it is meant to detect.
   *
   * This app lower-cases `owning_party` in three places (`toSelection`, `groupAddOnsByParty`,
   * `buildAddOnSubmission`), so the catalog's original casing survives ONLY here, on the objects the
   * resource read returned.
   */
  const formItems = useMemo(() => {
    const selectedSection = partySections.find(section => section.key === selectedPartyKey);

    if (!selectedSection) {
      return [];
    }

    const selectedIds = new Set(submittableSelection.map(item => item.id));

    return selectedSection.addOns
      .filter(addOn => selectedIds.has(addOn.id) && addOn.owning_party)
      .map(addOn => ({ catalogItemUuid: addOn.id, ownerCompanyUuid: addOn.owning_party as string }));
  }, [partySections, selectedPartyKey, submittableSelection]);

  /*
   * ⚠️ Keyed on the ITEM SET, not on the selection: a quantity change must not re-resolve. The read
   * takes catalog item uuids and their owners, so quantity cannot alter its answer, and re-firing on
   * every tap of a stepper would put a request behind each one.
   */
  const formItemsKey = useMemo(
    () =>
      formItems
        /*
         * ⛔ THE OWNER IS PART OF THE KEY, NOT JUST THE ITEM. A catalog product can be reassigned to a
         * different company, and a resource re-fetch then returns the SAME item uuid under a new
         * owner. Keyed on the item alone, nothing re-resolves: the picker shows the new provider
         * while the old provider's template stays live, so their answers travel with an order that
         * is no longer theirs. Keyed on the pair, a reassignment is a different question set and gets
         * one.
         */
        .map(item => `${item.catalogItemUuid}:${item.ownerCompanyUuid}`)
        .sort()
        .join(','),
    [formItems],
  );

  useEffect(() => {
    if (!isEligible || !buildingUuid || !bookingId) {
      return;
    }

    /*
     * ⛔ NOTHING LEFT TO RESOLVE MEANS CLEAR, NOT "LEAVE IT AS IT WAS". The tenant has emptied their
     * selection, or swapped to items that carry no owning party. Returning early here would leave the
     * previous template and definition live — so a form the tenant can no longer see stays answerable,
     * and `onSubmit` would file those answers against whatever they order next.
     */
    if (!formItemsKey) {
      dispatch(clearAddOnForm());
      return;
    }

    dispatch(
      resolveAddOnForm.request({
        buildingUuid,
        lang: locale,
        items: formItems,
        bookingId,
        requestKey: formItemsKey,
      }),
    );
    /*
     * `formItems` is deliberately absent from the deps: it is a fresh array on every render, and
     * `formItemsKey` is the value that actually decides whether the answer could differ.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isEligible, buildingUuid, bookingId, locale, formItemsKey]);

  const hasForm = Boolean(form.templateUuid && form.definition);

  /*
   * ⛔ PENDING IS NOT THE FAIL-OPEN CASE, and conflating the two loses answers silently. Everywhere
   * else a form that cannot be resolved means "no form, carry on" — but that is for a resolve that
   * FAILED. One still running has not answered yet, and `hasForm` is false throughout it. Without
   * this, a tenant who taps Continue quickly enough submits before their vendor's questions have
   * arrived, with `form_response: null`, and the vendor gets an order with no answers on it.
   */
  const isResolvingForm = form.status === ACTION_STATUSES.PENDING;

  /*
   * Deliverable 6. Resolved back to the rendered add-ons so the notice can NAME what is wrong rather
   * than printing uuids at a tenant.
   */
  const unresolvedAddOns = useMemo(
    () => addOns.filter(addOn => form.unresolvedItemIds.includes(addOn.id)),
    [addOns, form.unresolvedItemIds],
  );

  /*
   * RES-1334. Not covered by `form.isValid`: the renderer is controlled, so it normalises `"a"` to
   * `null` before validating and reports valid on the value it invented, while the answer we are
   * about to submit is still `"a"`. See `validate-form-answers.ts`.
   */
  const invalidAnswerKeys = useMemo(
    () => findInvalidAnswerKeys(form.definition, form.values),
    [form.definition, form.values],
  );

  const onFormChange = useCallback(
    (values: Record<string, unknown>) => dispatch(setAddOnFormValues(values)),
    [dispatch],
  );

  const onFormValidate = useCallback(
    (state: FormRendererValidationState) => dispatch(setAddOnFormValidity(state.isValid)),
    [dispatch],
  );

  const onBackToPicker = useCallback(() => setStep('picker'), []);

  /*
   * ⛔ FORWARD, NEVER BACK. The order was written before the payment screen rendered, so there is
   * nothing to return to and edit — and a tenant who dismisses the sheet must not land on a screen
   * implying their add-ons were lost. An unpaid charge is the vendor's to chase; a confirmation that
   * contradicts what was ordered is not recoverable.
   */
  /*
   * ⚠️ THE SHEET IS CLOSED — not "the payment succeeded". It is the one exit from billing's overlay
   * and it is the same exit for paid, declined, expired and abandoned alike, so all it may do is move
   * the tenant on and note that the surface is gone. The flag it raises buys one bounded look at the
   * work order, for the ending where the charge went through and `onPaymentComplete` never arrived.
   */
  const onPaymentSettled = useCallback(() => {
    setHasDismissedPayment(true);
    setStep('confirmation');
  }, []);

  /*
   * Absolute, and the query is not decoration. This step lives under whichever of PaymentContent's nine
   * hosts the tenant came through, so the repo's `${pathname}/bookings` idiom matches no route from
   * here, and `useOwnerParams` takes the owner scope entirely from the query.
   *
   * Both booking ids are stripped: `useUpcomingBookings` auto-opens a receipt wherever it sees one.
   */
  const { enable2WayChatRedesignResourceBooking } = useFlags();
  const landOnMyBookings = usePostBookingLanding();
  const onViewMyBookings = useCallback(() => {
    const scoped = pick(
      searchParams ?? {},
      QUERY_PARAMS.filter(param => param !== 'bookingId' && param !== BOOKING_ADD_ONS_PARAM),
    );

    /*
     * RES-1407: under the 2-way chat redesign, with this booking selected -- where it now lives after
     * the order, the same place every other way out of a booking just made leads.
     */
    /*
     * The frozen order's booking when the live one is gone: the submit re-reads the booking, and this
     * runs from the confirmation, which is exactly the state where the live read can be empty. Without
     * it the redesign branch was skipped and the tenant got the bare list push, nothing selected.
     */
    const target = booking ?? orderedAddOns?.booking;

    if (enable2WayChatRedesignResourceBooking) {
      /*
       * The landing, not a bare push: from here back would otherwise return into the add-on step, or
       * on to the booking flow beneath it. `usePostBookingLanding` replaces that with the landing page
       * and puts My bookings on top, with this booking selected (the upcoming tab if none is known).
       */
      landOnMyBookings({ transactionUuid: target?.uuid, bookingId: target?.details?.id, params: searchParams });

      return;
    }

    dispatch(push(`/bookings?${qs.stringify(scoped)}`));
  }, [dispatch, searchParams, enable2WayChatRedesignResourceBooking, booking, orderedAddOns, landOnMyBookings]);

  /*
   * Every way out of this step that is not "forward": the picker's nothing-selected path, "Back to
   * booking" on the confirmation, and the exit control in the modal header.
   *
   * `replace`, not `goBack()` — do not "fix" this. The bounce effect and a pasted `add-on-request` URL
   * both reach here with nothing sensible behind them. One destination that is always correct beats
   * two behaviours keyed on how the tenant arrived; the cost is a repeated back step on a screen they
   * are leaving anyway.
   *
   * ⛔ ONLY the add-on marker is dropped, and the rest of the query is carried verbatim. Dropping it
   * matters because `useUpcomingBookings` auto-opens a receipt wherever it sees a booking id; keeping
   * the rest matters because the receipt still honours params like `closeAfterBooking`, and filtering
   * through an allow-list silently loses the ones it has not heard of.
   */
  const toReceipt = useCallback(
    (extra: Record<string, string> = {}) => {
      const query = qs.stringify({ ...omit(searchParams ?? {}, BOOKING_ADD_ONS_PARAM), ...extra });

      dispatch(replace(`${bookingPath}${query ? `?${query}` : ''}`));
    },
    [dispatch, bookingPath, searchParams],
  );

  /* The back arrow, and "Back to booking" on the confirmation: the booking, as the tenant left it. */
  const onBackToBooking = useCallback(() => toReceipt(), [toReceipt]);

  /*
   * "Continue without add-ons" — an ANSWER, not a retreat, which is why it does not share the handler
   * above. Without the marker the receipt re-qualifies for the offer (nothing was ordered, so the
   * booking is still eligible) and puts the same card back in front of the tenant who just declined
   * it. Carrying it sends them to the receipt proper, which is where declining should land.
   */
  const onSkip = useCallback(() => toReceipt({ [ADD_ONS_DECLINED_PARAM]: 'true' }), [toReceipt]);

  const onSubmit = useCallback(() => {
    /* ⛔ Re-checked at the point of action, not only at render — a stale screen must not be submittable. */
    if (!isEligible || !bookingId || !resourceId || !ownerType || !ownerUuid) {
      return;
    }

    /*
     * ⛔ The validity gate re-checked here as well as on the button. The shared library's `Counter`
     * taught this page that a `disabled` prop can be cosmetic — it greys the icon while the click
     * handler stays bound — so neither half of the gate may depend on the other being right.
     */
    if (!form.isValid) {
      return;
    }

    /* RES-1334. Blocked rather than warned: the tenant clears this by retyping the value. */
    if (invalidAnswerKeys.length > 0) {
      return;
    }

    /* RES-1333, and the same reasoning as the validity gate above: the disable alone is not the gate. */
    if (rangeViolations.length > 0) {
      return;
    }

    const add_ons = buildAddOnSubmission(submittableSelection);

    dispatch(
      submitBookingAddOns.request({
        ownerType,
        ownerUuid,
        resourceId: String(resourceId),
        bookingId,
        add_ons,
        /*
         * Built from the menu, never from `add_ons`: `buildAddOnSubmission` lower-cases the owner uuid,
         * and work-orders matches that value by JS string identity rather than by collation.
         */
        ...(buildingUuid && rangeItems.length > 0 ? { rangeCheck: { buildingUuid, items: rangeItems } } : {}),
        /*
         * ⭐ ALWAYS EXPLICIT — the object when a form applies, `null` when none does. NEVER omitted.
         *
         * helix reads three states off this key, not two: absent leaves stored answers untouched,
         * `null` clears them, an object replaces them. And "untouched" is itself conditional — it
         * inherits the stored response only when the selection resolves to exactly one owning company
         * that already carries one, and clears it otherwise. So an omitted key means two different
         * things depending on server state this client cannot see. Being explicit removes the question.
         *
         * ⛔ A present OBJECT with an empty selection is a 422, which is why this is gated on
         * `add_ons.length` and not merely on whether a form was rendered — clearing the last add-on
         * must send `null`, the legal way to clear both.
         */
        form_response: form.templateUuid && add_ons.length ? buildFormResponse(form.templateUuid, form.values) : null,
      }),
    );
  }, [
    dispatch,
    isEligible,
    bookingId,
    resourceId,
    ownerType,
    ownerUuid,
    submittableSelection,
    form.templateUuid,
    form.values,
    form.isValid,
    invalidAnswerKeys,
    rangeViolations,
    buildingUuid,
    rangeItems,
  ]);

  /*
   * The picker's CTA has three destinations and the order matters: nothing selected is a skip, a
   * selection with questions opens them, and a selection without questions submits straight away —
   * which is the common path, since most bookings carry no vendor form at all.
   */
  const onContinue = useCallback(() => {
    /* Nothing to continue to once the order is placed, and re-submitting would duplicate it. */
    if (step === 'confirmation') {
      return;
    }

    if (submittableSelection.length === 0) {
      onSkip();
      return;
    }

    /* Ignore the tap until the vendor's questions are known. The CTA is disabled too; neither half
     * may depend on the other being right. */
    if (isResolvingForm) {
      return;
    }

    /*
     * ⛔ `step` IS PART OF THIS DECISION, and leaving it out makes the questions screen a dead end:
     * its CTA calls this same handler, so without the check it would see `hasForm` and set the step
     * it is already on. The tenant fills the form in and the button does nothing.
     */
    if (hasForm && step === 'picker') {
      setStep('questions');
      return;
    }

    onSubmit();
  }, [submittableSelection.length, isResolvingForm, hasForm, step, onSkip, onSubmit]);

  /*
   * ⚠️ A tenant can be standing on the questions screen when the form goes away — clearing the last
   * add-on, or switching to a party with no questions. Returning them to the picker keeps the screen
   * from rendering an empty form with a CTA that submits nothing.
   */
  useEffect(() => {
    if (step === 'questions' && !hasForm) {
      setStep('picker');
    }
  }, [step, hasForm]);

  /* The correction is made with the steppers, so a refusal raised from the questions screen goes back. */
  useEffect(() => {
    if (rangeViolations.length > 0) {
      setStep('picker');
    }
  }, [rangeViolations]);

  useEffect(() => {
    if (submitStatus !== ACTION_STATUSES.FULFILLED || !booking) {
      return;
    }

    /*
     * The snapshot reads values closed over by this render — `submittableSelection`, `estimateTotal` —
     * so the dispatches below genuinely cannot affect it, whatever order they sit in. It is written
     * first for reading, not for safety.
     *
     * ⚠️ That distinction is worth keeping straight, because it was checked: moving this below the
     * dispatches kills no test, and the reason is that it is a non-property rather than an untested one.
     * A comment claiming a hazard here would make the next person preserve an ordering that means
     * nothing. What the snapshot genuinely protects against is the LATER state — see `OrderedAddOns`.
     */
    setOrderedAddOns({
      booking,
      partyName: selectedPartyName,
      partyLogoUrl: selectedPartyLogoUrl,
      lines: submittableSelection,
      total: estimateTotal,
      currency: estimateCurrency,
      hasUnpriceable,
    });
    /*
     * ⛔ THE ORDER IS PLACED EITHER WAY — this only decides which screen the tenant lands on. The
     * add-ons are written by the submit above; payment is a separate charge against the linked
     * request, so a tenant who never reaches the payment screen still has their order.
     *
     * ⚠️ DECIDED FROM THIS ORDER, NOT FROM THE PAYMENT READ. It used to branch on `payment`, which at
     * this instant could only hold an answer fetched BEFORE the order existed — necessarily "nothing
     * to pay" — so payment was skipped every time. The charge cannot be known yet: the linked request
     * is created asynchronously after this. What IS known is whether the tenant ordered something
     * that costs money, and that is the right question to route on.
     *
     * ⛔ NOT `estimateCurrency !== null`, which was the first version of this and is a DIFFERENT
     * QUESTION. That goes null for two unrelated reasons, and only one of them means "no charge":
     *
     *   - a quote-required line — work-orders parks the WHOLE request at `pending_pricing` when any
     *     line is QUOTE_REQUIRED, so no session is ever opened and the confirmation is the right
     *     landing. The quote flow is RES-1357's.
     *   - a selection spanning currencies — a DISPLAY limit, nothing more. Every line is priced, the
     *     request is fully automatic, and a session does arrive; suppressing payment there would drop
     *     a real charge on the floor. Flagged by Greptile on #1936.
     */
    /*
     * ⛔ AND NOT WHILE THE BOOKING ITSELF IS AWAITING APPROVAL, which is a THIRD reason no session
     * can arrive — verified in work-orders-service master, not inferred:
     *
     *   - `work-orders-tenants.service.ts` creates the request with
     *     `sourceApprovalState: request.bookingPendingApproval ? PENDING : null`.
     *   - `automatic-checkout.service.ts` refuses unless that field is `null` or `APPROVED`.
     *   - NOTHING ON MASTER EVER WRITES `APPROVED`. Grepping the whole service finds two readers and
     *     no writer, so the CDC "approval released" branch that would re-run checkout cannot fire.
     *
     * So for a `Pending` booking the session is not late, it is never coming, and treating the order
     * as payable buys a guaranteed 45 dead seconds behind a busy CTA before landing unpaid anyway.
     * ⚠️ REMOVE THIS once the consumer that releases the source approval ships (work-orders #856) —
     * until then the honest landing is the confirmation, with the estimate it already shows.
     *
     * `Pending` is deliberately eligible for add-ons in the first place (see `LIVE_BOOKING_STATUSES`):
     * the booking still holds its room, so ordering is right — only the CHARGE has to wait.
     */
    const isPendingApproval = booking?.details?.status === BookingStatus.PENDING;
    const isPayable = estimateTotal > 0 && !hasUnpriceable && !isPendingApproval;

    /*
     * ⛔ EVERY ORDER LANDS ON THE CONFIRMATION, PAYABLE OR NOT, and billing's sheet opens OVER it.
     *
     * Holding a payable order on the screen it was submitted from was tried and does not survive
     * this same handler: the two lines below reset the selection and re-read the booking — which is
     * what stops a second, duplicate order — and that re-read drops `formDefinition`, so the
     * questions screen fails its own render guard and falls through to the PICKER. Measured live:
     * the tenant watched their checkout screen turn back into a menu of the products they had just
     * ordered. An empty modal was the symptom being fixed; re-offering the order is worse than it.
     *
     * ⚠️ The confirmation is a REAL screen — it states what was ordered — so the sheet is no longer
     * floating over an empty surface with its own close control, which was the whole complaint.
     */
    /* ⛔ Captured here, while the booking is still in hand — see the state's own note. */
    setPayableBookingId(isPayable && booking?.details?.id ? Number(booking.details.id) : null);
    setStep('confirmation');

    dispatch(resetSubmitBookingAddOnsStatus());

    /*
     * ⛔⛔ THESE TWO LINES ARE WHAT STOPS A SECOND, DUPLICATE ORDER, AND NEITHER IS OPTIONAL.
     *
     * `bookingAddOnsEligibility`'s last condition — "the booking does not already carry add-ons" —
     * exists because the add-on rows have no uniqueness constraint and the write is a blind create.
     * Nothing used to make it fire: the reducer keeps the selection on success, the epic emits no
     * re-read, and claiming the slice for the same booking id is a no-op. Until RES-1326 that was
     * unreachable, because the detour was one-shot with no way back to the step.
     *
     * The receipt's prompt card IS a way back. So the order clears the selection, and re-reads the
     * booking so the condition finally bites — `resetBookingAddOns` covers a remount inside this
     * session, and the re-read covers a reload, where local state is gone.
     *
     * ⛔⛔ AND IT TAKES BOTH READS, BECAUSE THE TWO CONSUMERS LOOK AT DIFFERENT PLACES.
     * `getTransaction.success` writes only `transactions.transaction` — the single slot — which is what
     * the STEP prefers (see the note on `storeMatches`). The RECEIPT, and therefore the prompt card,
     * takes its booking from the `transactions` LIST via `useReceipt`'s `findTargetTransaction`, and
     * only `getTransactions.success` rewrites that. Refreshing the slot alone leaves the list entry
     * carrying no add-ons, so the card goes on offering the step for the booking just ordered — a dead
     * end that bounces, and a window in which re-selecting could file a second order.
     *
     * ⚠️ The two are not interchangeable: `ApiClient.getTransactions` hardcodes `is_upcoming=true`, so
     * a booking whose start has passed drops out of the list entirely. That is exactly why the step
     * keeps reading the slot rather than the list.
     */
    dispatch(resetBookingAddOns());
    dispatch(getTransaction.request(booking.uuid));
    dispatch(getTransactions.request({}));

    /*
     * The re-read brings back the ordered rows but not the money: the payment block is three async hops
     * away, so the card's read on mount lands in that gap and `addOnOrderActions(null)` draws nothing.
     * The mark has the card ask again, and this is the only moment that knows an order happened.
     */
    /*
     * The money as the CARD last read it. NOT `payment.payment`, the obvious source, which is null
     * on every amend: this flow's own payment query stays disabled until `payableBookingId` is set,
     * and that happens a few lines above in this same effect.
     *
     * Read before the invalidation below, and matched on the booking rather than taking the first
     * entry -- the key holds a NUMBER where `bookingId` here is a string.
     */
    const cachedFrom = queryClient
      .getQueriesData<OrderedAddOnsRead>({ queryKey: ['ordered-add-on-payment'] })
      .find(([key]) => key[2] === Number(bookingId))?.[1]?.payment;

    dispatch(
      markAddOnsOrdered({
        bookingId,
        at: Date.now(),
        /* Null, not `paymentIdentity(null)`: no baseline disarms the test, where a fingerprint of
         * nothing would arm it against a value the server can never answer with. */
        paymentKey: amendingFrom ?? (cachedFrom ? paymentIdentity(cachedFrom) : null),
      }),
    );

    /*
     * An amend re-asks neither money read on its own, and the disposition poll cannot rescue it: it
     * reads this same cache to decide whether to wait, so a stale settled payload says "nothing to
     * wait for". One forced read is what the poll starts from. RES-1317.
     *
     * By prefix, deliberately: `bookingId` here is a string and both keys hold the numeric
     * `payableBookingId`, so an exact key built from this one matches nothing and fails silently.
     */
    queryClient.invalidateQueries({ queryKey: ['add-on-payment'] });
    queryClient.invalidateQueries({ queryKey: ['ordered-add-on-payment'] });
  }, [
    dispatch,
    queryClient,
    amendingFrom,
    submitStatus,
    booking,
    bookingId,
    selectedPartyName,
    selectedPartyLogoUrl,
    submittableSelection,
    estimateTotal,
    estimateCurrency,
    /* Decides the landing step — a stale value here would route a payable order past payment. */
    hasUnpriceable,
    payment.checkoutSessionUuid,
    payment.isAwaitingSession,
  ]);

  /*
   * ⛔ THE SNAPSHOT WINS ONCE THERE IS ONE, and this is the quieter half of the same hazard as the
   * eligibility-bounce guard. `booking-add-ons.tsx` early-returns `null` on a falsy booking, so after
   * the order re-reads the booking and eligibility flips, suppressing the bounce alone would leave the
   * tenant on a blank screen — no navigation, no error, nothing to recover from.
   */
  const bookingForRender = orderedAddOns?.booking ?? (isEligible ? booking : undefined);

  return {
    booking: bookingForRender,
    bookingId,
    /* RES-1359. Both row surfaces date their lead-time line from it. Always set on a real booking. */
    bookingStart,
    addOns,
    partySections,
    selectedPartyKey,
    isPartyLocked,
    isKept,
    selectedCount: submittableSelection.length,
    estimateTotal,
    estimateCurrency,
    hasUnpriceable,
    quantityOf,
    minQuantityOf,
    onRemove,
    isAtMax,
    isFixedQuantity,
    isAwaitingRanges,
    payment,
    onPaymentSettled,
    onIncrease,
    onDecrease,
    onSkip,
    onSubmit,
    onPaymentCharged,
    isSubmitting: submitStatus === ACTION_STATUSES.PENDING,
    /* Only while the order is still ON this screen: once the sheet opens, the sheet is the wait. */
    isAwaitingPayment: payment.isAwaitingSession && !payment.checkoutSessionUuid,
    hasSubmitFailed: submitStatus === ACTION_STATUSES.REJECTED,
    rangeViolations,
    isLoading: isLoadingBooking || !isResourceSettled,
    step,
    hasForm,
    isResolvingForm,
    formDefinition: form.definition,
    formValues: form.values,
    isFormValid: form.isValid,
    unresolvedAddOns,
    hasInvalidAnswers: invalidAnswerKeys.length > 0,
    onFormChange,
    onFormValidate,
    onContinue,
    onBackToPicker,
    onBackToBooking,
    orderedAddOns,
    onViewMyBookings,
  };
};
